/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { Button, ButtonVariety, Modal, Select, toast } from '@sonarsource/echoes-react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { isDefined } from '~shared/helpers/types';
import { useJiraWorkTypes } from '../../hooks/useJiraWorkTypes';
import {
  useCreateJiraProjectBindingMutation,
  useJiraProjectsQuery,
  useUpdateJiraProjectBindingMutation,
} from '../../queries';
import { JiraProjectBinding } from '../../types';
import { JiraProjectWorkTypesGroup } from './JiraProjectWorkTypesGroup';

interface JiraBindingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  orgBindingId?: string;
  orgId?: string;
  projectBinding?: JiraProjectBinding;
  sonarProjectId: string;
  sonarProjectName: string;
}
const PROJECTS_LIMIT_SIZE = 1000;

function MaskedProjectName({ projectName }: Readonly<{ projectName: string | undefined }>) {
  return <span className="fs-mask">{projectName || ''}</span>;
}

export default function JiraProjectBindingDialog({
  isOpen,
  orgId = '',
  orgBindingId = '',
  onClose,
  projectBinding,
  sonarProjectId,
  sonarProjectName,
}: Readonly<JiraBindingDialogProps>) {
  const { data: jiraProjects = [], isLoading: loadingJiraProjects } = useJiraProjectsQuery(orgId, {
    enabled: isOpen,
  });

  const { mutateAsync: createJiraProjectBinding, isPending: creatingProjectBinding } =
    useCreateJiraProjectBindingMutation();
  const { mutateAsync: editJiraProjectBinding, isPending: editingProjectBinding } =
    useUpdateJiraProjectBindingMutation();
  const [selectedProject, setSelectedProject] = useState<string | undefined>();

  const {
    loadingWorkTypes,
    savingWorkTypes,
    setSelectedWorkTypeIds,
    workTypes,
    reset: resetWorkTypes,
    selectedWorkTypeIds,
    saveWorkTypes,
    someWorkTypeHasMandatoryFields,
  } = useJiraWorkTypes({
    jiraProjectKey: selectedProject,
    sonarOrganizationUuid: orgId,
    sonarProjectId,
    preselect: true,
  });

  const isEditMode = Boolean(projectBinding);
  const jiraProjectsOptions = useMemo(
    () =>
      jiraProjects
        .filter((p) => p.key !== projectBinding?.jiraProjectKey)
        .map((p) => ({
          value: p.key,
          label: `${p.name} (${p.key})`,
        })),
    [jiraProjects, projectBinding],
  );
  const boundJiraProjectName = useMemo(
    () => jiraProjects.find((p) => p.key === projectBinding?.jiraProjectKey)?.name ?? '',
    [jiraProjects, projectBinding],
  );
  const selectedProjectName = useMemo(
    () => jiraProjects.find((p) => p.key === selectedProject)?.name ?? '',
    [jiraProjects, selectedProject],
  );

  const handleConnect = async () => {
    const apiCall = isEditMode
      ? editJiraProjectBinding({
          sonarProjectId,
          jiraProjectKey: selectedProject as string,
        })
      : createJiraProjectBinding({
          sonarProjectId,
          jiraProjectKey: selectedProject as string,
          jiraOrganizationId: orgBindingId,
        });

    await apiCall;
    await saveWorkTypes();

    toast.success({
      description: (
        <FormattedMessage
          id={
            isEditMode
              ? 'project_settings.jira.binding.update_bind_success'
              : 'project_settings.jira.binding.bind_success'
          }
          values={{
            name: <span className="fs-mask">{selectedProjectName}</span>,
            sqProject: <MaskedProjectName projectName={sonarProjectName} />,
          }}
        />
      ),
      duration: 'medium',
    });
    handleClose();
  };

  const handleClose = () => {
    setSelectedProject(undefined);
    onClose();
    resetWorkTypes();
  };

  return (
    <Modal
      content={
        <>
          <Select
            ariaLabelledBy="binding-title"
            className="fs-mask"
            data={jiraProjectsOptions}
            isLoading={loadingJiraProjects}
            isNotClearable
            isSearchable
            limit={PROJECTS_LIMIT_SIZE}
            onChange={(e) => {
              setSelectedProject(e ?? undefined);
            }}
            value={selectedProject}
          />
          {selectedProject && (
            <JiraProjectWorkTypesGroup
              hasMandatoryFields={someWorkTypeHasMandatoryFields}
              isLoading={loadingWorkTypes}
              onChange={setSelectedWorkTypeIds}
              selectedWorkTypeIds={selectedWorkTypeIds}
              workTypes={workTypes}
            />
          )}
        </>
      }
      description={
        <FormattedMessage
          id={
            isEditMode
              ? 'project_settings.jira_binding.edit_binding.description'
              : 'project_settings.jira_binding.create_binding.description'
          }
          values={{ name: <span className="fs-mask">{boundJiraProjectName}</span> }}
        />
      }
      isOpen={isOpen}
      onOpenChange={handleClose}
      primaryButton={
        <Button
          isDisabled={
            !isDefined(selectedProject) ||
            creatingProjectBinding ||
            editingProjectBinding ||
            loadingWorkTypes ||
            savingWorkTypes
          }
          isLoading={creatingProjectBinding || editingProjectBinding || savingWorkTypes}
          onClick={handleConnect}
          variety={ButtonVariety.Primary}
        >
          <FormattedMessage id={isEditMode ? 'update_verb' : 'connect'} />
        </Button>
      }
      secondaryButton={
        <Button onClick={handleClose} variety={ButtonVariety.Default}>
          <FormattedMessage id="cancel" />
        </Button>
      }
      title={
        <span id="binding-title">
          <FormattedMessage
            id={
              isEditMode
                ? 'project_settings.jira_binding.edit_binding.title'
                : 'project_settings.jira_binding.create_binding.title'
            }
          />
        </span>
      }
    />
  );
}
